import { defineStore } from 'pinia';
import i18n from '@/common/i18n';
import LoginRepository from '@/repositories/loginRepository';
import VendorRepository from '@/repositories/vendorRepository';
import cognitoRepository from '@/repositories/common/cognitoRepository';
import apiResponseErrorService from '@/commonProject/service/apiResponseErrorService';

// Utis
import utils from '@/commonProject/helpers/utils';

export const useLoginStore = defineStore({
    id: 'login',
    state: () => ({
        userId: '',
        nameKjLs: '',
        nameKjFs: '',
        vendorId: '',
        vendorType: '',
        phoneNumber: '',
        mailAddress: '',
        maintenanceMode: false,
        userKeyNoSupport: false, // ユーザーキー番号
        userKeyNoNm: null, // ユーザーキー番号名称
        regulationPk: '',
        firstFlag: false, // VueでStoreを読み込ませるための任意要素。ログイン画面のonMoutnedでVueからStoreを読みだすためにのみ利用する。他の画面や機能では利用していない。
    }),
    getters: {
        getUserName() {
            return this.nameKjLs + ' ' + this.nameKjFs;
        },
        getVendorId() {
            return this.vendorId;
        },
        getVendorType() {
            return this.vendorType;
        },
        getMaintenanceMode() {
            return this.maintenanceMode;
        },
        getUserKeyNoSupport() {
            return this.userKeyNoSupport;
        },
        getUserKeyNoNm() {
            return this.userKeyNoNm;
        },
    },
    actions: {
        setVendorId(str) {
            this.vendorId = str;
        },

        setUserKeyNoSupport(str) {
            this.userKeyNoSupport = str;
        },

        setUserKeyNoNm(str) {
            this.userKeyNoNm = str;
        },
        reset() {
            this.userId = '';
            this.nameKjLs = '';
            this.nameKjFs = '';
            this.vendorId = '';
            this.vendorType = '';
            this.phoneNumber = '';
            this.mailAddress = '';
            this.maintenanceMode = false;
            this.userKeyNoSupport = false;
            this.userKeyNoNm = null;
            this.regulationPk = '';
            this.firstFlag = false; // VueでStoreを読み込ませるための任意要素。ログイン画面のonMoutnedでVueからStoreを読みだすためにのみ利用する。他の画面や機能では利用していない。
        },

        /**
         * ログアウトAPI
         */
        logoutAction() {
            this.$reset();
        },

        /**
         * メンテナンス状態取得APIをコールする。
         * @param {Object} queryParam
         * @returns response
         */
        async getMaintenanceModeAction(API_URL, queryParam) {
            const loginRepository = new LoginRepository();
            try {
                const response = await loginRepository.getMaintenanceModeApi(API_URL, queryParam);
                const specialApiError = apiResponseErrorService.isStatus200HasError(response);
                if (specialApiError.status) {
                    return { success: false, errorMessage: specialApiError.message, redirect: false, redirectUrl: '' };
                }
                this.maintenanceMode = response.data.Maintenance;
                return { success: true };
            } catch (e) {
                return {
                    success: false,
                    errorMessage: apiResponseErrorService.errorMessage(e.response, 'get', i18n.global),
                    redirect: apiResponseErrorService.isLogoutToRedirect(e.response),
                    redirectUrl: apiResponseErrorService.logoutToRedirectUrl(e.response),
                };
            }
        },

        /**
         * ベンダー情報取得APIをコールする。
         * @param {Object} queryParam
         * @returns response
         */
        async getVendorInfoAction(API_URL, queryParam) {
            const vendorRepository = new VendorRepository();
            try {
                const response = await vendorRepository.getVendorApi(API_URL, queryParam);
                const specialApiError = apiResponseErrorService.isStatus200HasError(response);
                if (specialApiError.status) {
                    return { success: false, errorMessage: specialApiError.message, redirect: false, redirectUrl: '' };
                }
                return { success: true, responseBody: response.data };
            } catch (e) {
                return {
                    success: false,
                    errorMessage: apiResponseErrorService.errorMessage(e.response, 'get', i18n.global),
                    redirect: apiResponseErrorService.isLogoutToRedirect(e.response),
                    redirectUrl: apiResponseErrorService.logoutToRedirectUrl(e.response),
                };
            }
        },

        /**
         * ユーザー情報取得APIをコールする。
         * @param {Object} queryParam
         * @returns response
         */
        async getLoginUserInfoAction(API_URL, queryParam) {
            //const cognitoRepository = new CognitoRepository();
            const cognitoIdTokenKeyName = await cognitoRepository.getTokenKey();
            const idToken = localStorage.getItem(cognitoIdTokenKeyName);

            const loginRepository = new LoginRepository();
            try {
                const response = await loginRepository.getLoginUserInfoApi(API_URL, queryParam, idToken);
                const specialApiError = apiResponseErrorService.isStatus200HasError(response);
                if (specialApiError.status) {
                    return { success: false, errorMessage: specialApiError.message, redirect: false, redirectUrl: '' };
                }
                this.userId = response.data.UserId;
                this.nameKjLs = response.data.NameKjLs;
                this.nameKjFs = response.data.NameKjFs;
                this.vendorType = response.data.VendorType;
                this.userKeyNoSupport = response.data.UserKeyNoSupport;
                this.userKeyNoNm = response.data.UserKeyNoNm;
                this.phoneNumber = response.data.PhoneNumber;
                this.mailAddress = response.data.MailAddress;

                return { success: true, responseBody: response.data };
            } catch (e) {
                return {
                    success: false,
                    errorMessage: apiResponseErrorService.errorMessage(e.response, 'get', i18n.global),
                    redirect: apiResponseErrorService.isLogoutToRedirect(e.response),
                    redirectUrl: apiResponseErrorService.logoutToRedirectUrl(e.response),
                };
            }
        },

        /**
         * ユーザー状態確認(ログイン)APIをコールする。
         * @param {Object} queryParam
         * @returns response
         */
        async getUserStatusByLoginAction(API_URL, queryParam) {
            //const cognitoRepository = new CognitoRepository();
            const cognitoIdTokenKeyName = await cognitoRepository.getTokenKey();
            const idToken = localStorage.getItem(cognitoIdTokenKeyName);

            const loginRepository = new LoginRepository();
            try {
                const response = await loginRepository.getUserStatusByLoginActionApi(API_URL, queryParam, idToken);
                const specialApiError = apiResponseErrorService.isStatus200HasError(response);
                if (specialApiError.status) {
                    return { success: false, errorMessage: specialApiError.message, redirect: false, redirectUrl: '' };
                }
                this.regulationPk = response.data.RegulationPk;
                return { success: true, responseBody: response.data };
            } catch (e) {
                return {
                    success: false,
                    errorMessage: apiResponseErrorService.errorMessage(e.response, 'get', i18n.global),
                    redirect: apiResponseErrorService.isLogoutToRedirect(e.response),
                    redirectUrl: apiResponseErrorService.logoutToRedirectUrl(e.response),
                };
            }
        },

        /**
         * ユーザーログイン完了APIをコールする。
         * @param {Object} requestBody
         * @returns response
         */
        async postUserLoginDateUpdateAction(API_URL, requestBody) {
            const cognitoIdTokenKeyName = await cognitoRepository.getTokenKey();
            const idToken = localStorage.getItem(cognitoIdTokenKeyName);

            const loginRepository = new LoginRepository();
            try {
                // ユーザーログイン完了APIをコールする。
                const response = await loginRepository.postUserLoginDateUpdateApi(API_URL, requestBody, idToken);
                const specialApiError = apiResponseErrorService.isStatus200HasError(response);
                if (specialApiError.status) {
                    return { success: false, errorMessage: specialApiError.message, redirect: false, redirectUrl: '' };
                }
                return { success: true };
            } catch (e) {
                return {
                    success: false,
                    errorMessage: apiResponseErrorService.errorMessage(e.response, 'get', i18n.global),
                    redirect: apiResponseErrorService.isLogoutToRedirect(e.response),
                    redirectUrl: apiResponseErrorService.logoutToRedirectUrl(e.response),
                };
            }
        },

        /**
         * ユーザーパスワード更新完了APIをコールする。
         * @param {Object} requestBody
         * @returns response
         */
        async postUserLoginPasswordUpdateedDateUpdateAction(API_URL, requestBody) {
            const cognitoIdTokenKeyName = await cognitoRepository.getTokenKey();
            const idToken = localStorage.getItem(cognitoIdTokenKeyName);

            const loginRepository = new LoginRepository();
            try {
                // ユーザーパスワード更新完了APIをコールする。
                const response = await loginRepository.postUserLoginPasswordUpdateedDateUpdateApi(API_URL, requestBody, idToken);
                const specialApiError = apiResponseErrorService.isStatus200HasError(response);
                if (specialApiError.status) {
                    return { success: false, errorMessage: specialApiError.message, redirect: false, redirectUrl: '' };
                }
                return { success: true };
            } catch (e) {
                return {
                    success: false,
                    errorMessage: apiResponseErrorService.errorMessage(e.response, 'get', i18n.global),
                    redirect: apiResponseErrorService.isLogoutToRedirect(e.response),
                    redirectUrl: apiResponseErrorService.logoutToRedirectUrl(e.response),
                };
            }
        },

        /**
         * cognitoログインAPI
         * @param {*} req
         * @returns response
         */
        async cognitoLoginAction(Username, Password) {
            try {
                const response = await cognitoRepository.cognitoLoginApi(Username, Password);
                return response;
            } catch (e) {
                return e;
            }
        },

        /**
         * Cognito Id Tokenの有効期限の判定
         * @param {*} req
         * @returns response
         */
        async isTokenValid() {
            //const cognitoRepository = new CognitoRepository();
            const cognitoToken = await cognitoRepository.checkTokenExpiration();
            if (utils.isNull(cognitoToken)) {
                return false;
            } else {
                return true;
            }
        },

        /**
         * SSOログイン用。
         * Cognito Id Tokenの有効期限の判定
         * @param {*} req
         * @returns response
         */
        async isTokenValidCheckByCallApi() {
            //const cognitoRepository = new CognitoRepository();
            const cognitoToken = await cognitoRepository.checkTokenExpiratioByCallApi();
            if (utils.isNull(cognitoToken)) {
                return false;
            } else {
                return true;
            }
        },

        /**
         * Cognitoセッションを破棄し、ログアウトする。
         * @param なし
         * @returns なし
         */
        async cognitoLogoutAction() {
            //const cognitoRepository = new CognitoRepository();
            await cognitoRepository.logout();
        },

        /**
         * cognitoログインAPI
         * @param {*} req
         * @returns response
         */
        async cognitoTokenCallAction(code, redirectUrl) {
            try {
                const response = await cognitoRepository.token_call(code, redirectUrl);
                return response;
            } catch (e) {
                return e;
            }
        },

        /**
         * ha256アルゴリズムでhash値を取得
         * @returns hashHex
         */
        async sha256() {
            // 「年月日時分秒ミリ秒」を取得
            const currentDate = new Date();
            const date = currentDate.getFullYear() + '-' + ('0' + (currentDate.getMonth() + 1)).slice(-2) + '-' + ('0' + currentDate.getDate()).slice(-2) + ' ' + ('0' + currentDate.getHours()).slice(-2) + ':' + ('0' + currentDate.getMinutes()).slice(-2) + ':' + ('0' + currentDate.getSeconds()).slice(-2) + '.' + currentDate.getMilliseconds();
            // UUIDを取得
            const uuid = self.crypto.randomUUID();
            // 文字列をUint8Arrayにエンコードする
            const encoder = new TextEncoder();
            const msgUint8 = encoder.encode(date + '_' + uuid);
            // エンコードされたメッセージのSHA-256ハッシュを計算する
            // ここでWeb Crypto APIを使用している
            const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
            // SHA-256ハッシュの計算結果(hashBuffer)はArrayBufferなので、
            // それをUint8Arrayに変換し、16進数の文字列に変換する
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
            // SHA-256ハッシュの16進数の文字列を返す
            return hashHex;
        },
    },
    persist: {
        // ローカルストレージに保存する
        storage: localStorage,
    },
});
